/**
 * Secure Storage Utility
 *
 * Provides encrypted storage for sensitive OAuth data with automatic expiration.
 * Uses AES-GCM encryption to protect data at rest in sessionStorage.
 *
 * SECURITY NOTES:
 * - This mitigates but does not eliminate XSS risks
 * - For highest security, use HTTP-only cookies via server-side token exchange
 * - Data is encrypted with a session-specific key derived from crypto.getRandomValues
 */

// Generate a session-specific encryption key
let encryptionKey: CryptoKey | null = null;

async function getEncryptionKey(): Promise<CryptoKey> {
  if (encryptionKey) return encryptionKey;

  // Check if we have a stored key in memory (survives page navigation but not tab close)
  const storedKeyData = sessionStorage.getItem('_hs_key');

  if (storedKeyData) {
    try {
      const keyData = JSON.parse(storedKeyData);
      encryptionKey = await crypto.subtle.importKey(
        'raw',
        new Uint8Array(keyData),
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
      );
      return encryptionKey;
    } catch {
      // Key corrupted, generate new one
    }
  }

  // Generate new key
  encryptionKey = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );

  // Export and store the key (still in sessionStorage, but separate from data)
  const exportedKey = await crypto.subtle.exportKey('raw', encryptionKey);
  sessionStorage.setItem('_hs_key', JSON.stringify(Array.from(new Uint8Array(exportedKey))));

  return encryptionKey;
}

async function encrypt(data: string): Promise<string> {
  const key = await getEncryptionKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encodedData = new TextEncoder().encode(data);

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encodedData
  );

  // Combine IV and ciphertext
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(ciphertext), iv.length);

  return btoa(String.fromCharCode(...combined));
}

async function decrypt(encryptedData: string): Promise<string> {
  const key = await getEncryptionKey();
  const combined = new Uint8Array(
    atob(encryptedData).split('').map(c => c.charCodeAt(0))
  );

  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);

  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  );

  return new TextDecoder().decode(decrypted);
}

interface SecureStorageItem {
  data: string;
  expires: number;
  fingerprint: string;
}

// Generate a browser fingerprint for additional validation
function getBrowserFingerprint(): string {
  const data = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
  ].join('|');

  // Simple hash
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(36);
}

/**
 * Securely store sensitive data with encryption and expiration
 * @param key Storage key
 * @param value Value to store
 * @param ttlMs Time to live in milliseconds (default: 5 minutes)
 */
export async function secureSet(key: string, value: string, ttlMs: number = 5 * 60 * 1000): Promise<void> {
  const item: SecureStorageItem = {
    data: await encrypt(value),
    expires: Date.now() + ttlMs,
    fingerprint: getBrowserFingerprint(),
  };

  sessionStorage.setItem(`_sec_${key}`, JSON.stringify(item));
}

/**
 * Retrieve and decrypt securely stored data
 * @param key Storage key
 * @returns Decrypted value or null if expired/invalid
 */
export async function secureGet(key: string): Promise<string | null> {
  const stored = sessionStorage.getItem(`_sec_${key}`);
  if (!stored) return null;

  try {
    const item: SecureStorageItem = JSON.parse(stored);

    // Check expiration
    if (Date.now() > item.expires) {
      secureRemove(key);
      return null;
    }

    // Check fingerprint
    if (item.fingerprint !== getBrowserFingerprint()) {
      secureRemove(key);
      return null;
    }

    return await decrypt(item.data);
  } catch {
    secureRemove(key);
    return null;
  }
}

/**
 * Remove securely stored data
 * @param key Storage key
 */
export function secureRemove(key: string): void {
  sessionStorage.removeItem(`_sec_${key}`);
}

/**
 * Clear all secure storage items
 */
export function secureClearAll(): void {
  const keysToRemove: string[] = [];

  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith('_sec_') || key === '_hs_key') {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => sessionStorage.removeItem(key));
  encryptionKey = null;
}
